/*------------------------------------------------------------------------------
   About      : QA status + publish actions for one insight
                (epic DA-697, II-FE-5)

                Publish enablement comes from the workflow ACTION table
                (`actions[currentStatus]` containing PUBLISH), not an inline
                `=== READY_TO_PUBLISH` comparison — so the rule lives in one
                place and follows the server if it changes.

                Each control is gated on its own BFF feature: proxypulse splits
                read / write / publish, and publish and unpublish are
                CONTENT_PUBLISHERS-only. A non-publisher sees them hidden rather
                than getting a 403 on click.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { Alert, AlertTitle, Box, Button, Stack, Typography } from '@mui/material'
import PublishIcon     from '@mui/icons-material/Publish'
import UnpublishedIcon from '@mui/icons-material/Unpublished'

import { CONTENT_EDIT_ACTION,
         CONTENT_EDIT_STATUS,
         CONTENT_VERIFICATION_STATUS } from 'types/cms/frontendTypes'
import { UI_PAGES }        from 'framework/access/UiRegistry'
import { PermissionGate }  from 'components/access/PermissionGate'

import { InsightRecord,
         InsightReviewWorkflow } from '../types/insightTypes'
import { BatchOutcome }          from '../utils/insightBatchResult'
import { QaStatusSelect }        from './QaStatusSelect'

interface InsightWorkflowControlsProps {
  insight       : InsightRecord
  reviewWorkflow: InsightReviewWorkflow | null
  statusLabels  : Partial<Record<CONTENT_VERIFICATION_STATUS, string>>
  isRunning     : boolean
  outcome      ?: BatchOutcome
  /** Blocks workflow moves while the form has unsaved edits. */
  disabled     ?: boolean
  onChangeStatus: (next: CONTENT_VERIFICATION_STATUS) => void
  onPublish     : () => void
  onUnpublish   : () => void
  onBlocked    ?: (reason: string) => void
}

export const InsightWorkflowControls = ({
  insight,
  reviewWorkflow,
  statusLabels,
  isRunning,
  outcome,
  disabled,
  onChangeStatus,
  onPublish,
  onUnpublish,
  onBlocked,
}: InsightWorkflowControlsProps) => {

  /* Which actions the current status permits, straight from the workflow table.
     An absent table means we cannot know, so the actions stay hidden rather
     than being offered optimistically and failing server-side. */
  const permittedActions = reviewWorkflow?.[0]?.actions?.[insight.verificationStatus] ?? []
  const canPublish       = permittedActions.includes(CONTENT_EDIT_ACTION.PUBLISH)
  const canUnpublish     = permittedActions.includes(CONTENT_EDIT_ACTION.UNPUBLISH)
                           || insight.editStatus === CONTENT_EDIT_STATUS.PUBLISHED

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption" sx={{ color : 'text.secondary' }}>QA status</Typography>

        <QaStatusSelect
          current={insight.verificationStatus}
          transitions={insight.allowedTransitions ?? []}
          statusLabels={statusLabels}
          disabled={isRunning || disabled}
          onChange={onChangeStatus}
          onBlocked={(transition) => onBlocked?.(transition.blockedReason ?? '')}
        />

        {canPublish && (
          <PermissionGate require={UI_PAGES.NBI_INSIGHTS.ctas.publish.id}>
            <Button
              size="small" variant="contained" color="success"
              startIcon={<PublishIcon />}
              disabled={isRunning || disabled}
              onClick={onPublish}
            >
              Publish
            </Button>
          </PermissionGate>
        )}

        {canUnpublish && (
          <PermissionGate require={UI_PAGES.NBI_INSIGHTS.ctas.unpublish.id}>
            <Button
              size="small" variant="outlined"
              startIcon={<UnpublishedIcon />}
              disabled={isRunning || disabled}
              onClick={onUnpublish}
            >
              Unpublish
            </Button>
          </PermissionGate>
        )}
      </Stack>

      {disabled && (
        <Typography variant="caption" color="text.secondary" sx={{ display : 'block', mt : 0.5 }}>
          Save or undo your changes before moving this insight through the workflow.
        </Typography>
      )}

      {/* Publishing resets verificationStatus to TODO server-side, so say so
          rather than letting the status appear to have been reverted by a bug. */}
      {outcome?.kind === 'SUCCESS' && (
        <Alert severity="success" sx={{ mt : 1 }}>{outcome.message}</Alert>
      )}

      {outcome?.kind === 'PARTIAL' && (
        <Alert severity="warning" sx={{ mt : 1 }}>
          <AlertTitle>{outcome.message}</AlertTitle>
          {outcome.failures.map((failure) => (
            <Typography key={failure.key} variant="caption" sx={{ display : 'block' }}>
              <strong>{failure.key}</strong>: {failure.message}
            </Typography>
          ))}
        </Alert>
      )}

      {outcome?.kind === 'FAILURE' && (
        <Alert severity="error" sx={{ mt : 1 }}>
          <AlertTitle>{outcome.message}</AlertTitle>
          {outcome.failures.map((failure) => (
            <Typography key={failure.key} variant="caption" sx={{ display : 'block' }}>
              <strong>{failure.key}</strong>: {failure.message}
            </Typography>
          ))}
        </Alert>
      )}
    </Box>
  )
}

export default InsightWorkflowControls
